import { capitalize } from 'underscore.string';
import { I18n } from '../types'; // Importing the I18n interface

/**
 * A wrapper of i18n with schema.
 */
export class SchemaI18n {
  i18n: I18n;

  constructor({ i18n }: { i18n: I18n }) {
    this.i18n = i18n;
  }

  /**
   * Get the localized name of an item of an enum type.
   * @param {string} enumType - the enum type
   * @param {string} enumItem - the enum item of enumtype
   * @returns {string} - the localized string retrieved with the computed key.
   */
  enum(enumType: string, enumItem: string): string {
    return this.i18n.getString(`Schema_Enum_${capitalize(enumType.toString())}_${capitalize(enumItem.toString())}`);
  }

  /**
   * Get the localized string of the description of an item of an enum type.
   * @param {string} enumType - the enum type
   * @param {string} enumItem - the enum item of enumtype
   * @returns {string} - the localized string retrieved with the computed key.
   */
  enumDescription(enumType: string, enumItem: string): string {
    return this.i18n.getString(`Schema_Enum_${capitalize(enumType.toString())}_${capitalize(enumItem.toString())}_Description`);
  }

  /**
   * Get the localized name of a type
   * @param {string} type - the type
   * @returns {string} - the localized string retrieved with the computed key.
   */
  type(type: string): string {
    return this.i18n.getString(`Schema_${type}`);
  }

  /**
   * Get the localized name of a property of a type.
   * @param {string} type - the type
   * @param {string} property - the property
   * @returns {string} - the localized string retrieved with the computed key
   * or the result of type(property) if key not found.
   */
  property(type: string, property: string): string {
    return this.i18n.getString(`Schema_${type}_Property_${property}`)
      || this.type(property); // most non-primitive property has same name with it's type
  }

  /**
   * Get the localized string of a propery value of a type.
   * @param {string} type - the type
   * @param {string} property - the property
   * @param {string} value - the value
   * @returns {string} - the localized string retrieved with the computed key
   * or the result of enum(property, value) or undefined if nothing was found.
   */
  valueOrUndefined(type: string, property: string, value: string): string | undefined {
    return this.i18n.getString(`Schema_${type}_Property_${property}_Value_${capitalize(value.toString())}`)
      || this.enum(property, value)
      || undefined;
  }

  /**
   * Get the localized string of a propery value of a type, with fallback.
   * @param {string} type - the type
   * @param {string} property - the property
   * @param {string} value - the value
   * @returns {string} - the localized string retrieved with the computed key
   * or the result of enum(property, value) if key not found or the value itself.
   */
  value(type: string, property: string, value: string): string {
    return this.valueOrUndefined(type, property, value)
      // default to value itself, useful for already-localized value or no-need-to-localize value
      || String(value);
  }

  /**
   * Get the localized string of the description of a property value of a type.
   * @param {string} type - the type
   * @param {string} property - the property
   * @param {string} value - the value
   * @returns {string} - the localized string retrieved with the computed key.
   * or the result of enumDescription(property, value) if key not found or the value itself.
   */
  valueDescription(type: string, property: string, value: string): string {
    return this.i18n.getString(`Schema_${type}_Property_${property}_Value_${capitalize(value.toString())}_Description`)
      || this.enumDescription(property, value)
      || String(value);
  }
}
